import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormArray } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BeyondSupplier } from 'src/app/model/beyond-supplier';
import { BeyondSupplierInterface, Permission, Company } from 'src/app/model/beyond-supplier-interface';
import { FormControlDefinition } from 'src/app/model/form-control-definition';
import { BeyondSupplierService } from 'src/app/services/beyond-supplier/beyond-supplier.service';
import { GroupService } from 'src/app/services/group/group.service';
import { UserGroup } from 'src/app/model/user-group';

@Component({
  selector: 'app-edit-beyond-supplier-dialog',
  templateUrl: './edit-beyond-supplier-dialog.component.html',
  styleUrl: './edit-beyond-supplier-dialog.component.scss'
})
export class EditBeyondSupplierDialogComponent {
  /** Whether submit button was clicked */
  public isSubmitting: boolean = false;

  /** Available groups for the supplier's company */
  public availableGroups: UserGroup[] = [];

  /** Flag for loading groups */
  public groupsLoading: boolean = true;

  /** Available permissions for the supplier's company and role */
  public availablePermissions: Permission[] = [];

  /** Flag for loading permissions */
  public permissionsLoading: boolean = true;

  /** Form group for validation */
  public formGroup: FormGroup = new FormGroup({
    id: new FormControl(0, []),
    email: new FormControl("", BeyondSupplier.validators.get("email")),
    firstName: new FormControl("", BeyondSupplier.validators.get("firstName")),
    lastName: new FormControl("", BeyondSupplier.validators.get("lastName")),
    status: new FormControl("", []),
    group: new FormControl("", BeyondSupplier.validators.get("group")),
    permissions: new FormArray([]),
    companies: new FormArray([])
  });

  /** The form to render */
  public form: FormControlDefinition[] = [
    {
      title: "ID",
      name: "id",
      type: "number",
      placeholder: "0",
      control: this.formGroup.controls["id"],
      editable: false,
    },
    {
      title: "Email",
      name: "email",
      type: "email",
      placeholder: "Email Address",
      control: this.formGroup.controls["email"],
      editable: false,
    },
    {
      title: "First Name",
      name: "firstName",
      type: "",
      placeholder: "First Name",
      control: this.formGroup.controls["firstName"],
      editable: true,
    },
    {
      title: "Last Name",
      name: "lastName",
      type: "",
      placeholder: "Last Name",
      control: this.formGroup.controls["lastName"],
      editable: true,
    },
    {
      title: "Status",
      name: "status",
      type: "",
      placeholder: "Status",
      control: this.formGroup.controls["status"],
      editable: true,
    },
    {
      title: "Group",
      name: "group",
      type: "select",
      placeholder: "Group",
      control: this.formGroup.controls["group"],
      editable: true,
    },
  ];

  /**
   * Default constructor initializes the component with a dialog ref
   * @param dialogRef
   */
  constructor(
    public dialogRef: MatDialogRef<EditBeyondSupplierDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: {beyondSupplier: BeyondSupplier},
    private _groupService: GroupService,
  ) {
    const beyondSupplier = this.data?.beyondSupplier.copyOf();

    if (beyondSupplier == undefined) {
      this.cancel();
      return;
    }

    // Set form values
    this.formGroup.patchValue({
      id: beyondSupplier.id,
      email: beyondSupplier.email,
      firstName: beyondSupplier.firstName,
      lastName: beyondSupplier.lastName,
      status: beyondSupplier.status,
      group: beyondSupplier.group,
    });

    // Initialize permissions
    const permissionsArray = this.formGroup.get('permissions') as FormArray;
    beyondSupplier.permissions.forEach(permission => {
      permissionsArray.push(this.createPermissionFormGroup(permission));
    });

    // Initialize companies
    const companiesArray = this.formGroup.get('companies') as FormArray;
    beyondSupplier.companies.forEach(company => {
      companiesArray.push(this.createCompanyFormGroup(company));
    });

    // Fetch available groups for the supplier's primary company
    this.groupsLoading = true;
    const primaryCompanyId = beyondSupplier.primaryCompanyId;
    if (primaryCompanyId !== null) {
      this._groupService.getUserGroups(primaryCompanyId).subscribe({
        next: (groups: UserGroup[]) => {
          this.groupsLoading = false;
          this.availableGroups = groups;
        },
        error: (error) => {
          console.error('[EDIT-BEYOND-SUPPLIER]: Failed to fetch groups', error);
          this.groupsLoading = false;
          this.availableGroups = [];
        }
      });
    } else {
      console.warn('[EDIT-BEYOND-SUPPLIER]: No primary company ID found for supplier, cannot fetch groups');
      this.groupsLoading = false;
    }

    // If the user is an admin, add it to the list of permissions
    const adminPermission = beyondSupplier.permissions.find(p => p.permission.toLowerCase() === 'admin');
    if (adminPermission) {
      this.availablePermissions.push(adminPermission);
    }

    // Fetch available permissions for the supplier's primary company and role
    this.permissionsLoading = true;
    const primaryCompanyType = beyondSupplier.primaryCompanyType;
    if (primaryCompanyId !== null && primaryCompanyType) {
      // Get all permissions based on the primary company type
      this._groupService.getAllPermissions(primaryCompanyId, primaryCompanyType).subscribe({
        next: (permissions: Permission[]) => {
          this.permissionsLoading = false;
          this.availablePermissions.push(...permissions);
        },
        error: (error) => {
          console.error('[EDIT-BEYOND-SUPPLIER]: Failed to fetch permissions', error);
          this.permissionsLoading = false;
          this.availablePermissions = [];
        }
      });
    } else {
      console.warn('[EDIT-BEYOND-SUPPLIER]: No primary company ID or type found for supplier, cannot fetch permissions');
      this.permissionsLoading = false;
    }
  }

  /**
   * Get permissions FormArray
   */
  public get permissions(): FormArray {
    return this.formGroup.get('permissions') as FormArray;
  }

  /**
   * Get companies FormArray
   */
  public get companies(): FormArray {
    return this.formGroup.get('companies') as FormArray;
  }

  /**
   * Create a FormGroup for a permission
   */
  private createPermissionFormGroup(permission?: Permission, defaultCompanyId?: number): FormGroup {
    return new FormGroup({
      id: new FormControl(permission?.id || 0),
      permission: new FormControl(permission?.permission || ''),
      companyId: new FormControl(permission?.companyId || defaultCompanyId || 0)
    });
  }

  /**
   * Create a FormGroup for a company
   */
  private createCompanyFormGroup(company?: Company): FormGroup {
    return new FormGroup({
      id: new FormControl(company?.id || 0),
      companyId: new FormControl(company?.companyId || 0),
      companyType: new FormControl(company?.companyType || '')
    });
  }

  /**
   * Add a new permission to the FormArray
   */
  public addPermission() {
    // Pre-populate with primary company ID if available
    const beyondSupplier = this.data?.beyondSupplier;
    const defaultCompanyId = beyondSupplier?.primaryCompanyId || 0;
    this.permissions.push(this.createPermissionFormGroup(undefined, defaultCompanyId));
  }

  /**
   * Remove a permission from the FormArray
   */
  public removePermission(index: number) {
    this.permissions.removeAt(index);
    console.log(this.formGroup.value);
  }

  /**
   * Add a new company to the FormArray
   */
  public addCompany() {
    this.companies.push(this.createCompanyFormGroup());
  }

  /**
   * Remove a company from the FormArray
   */
  public removeCompany(index: number) {
    this.companies.removeAt(index);
  }
  
  /**
   * Cancel any changes and close the dialog
   */
  public cancel() {
    this.dialogRef.close(null);
  }

  /**
   * Save the changes to the beyond supplier and close the dialog
   */
  public save() {
    // Make sure this function can't run twice
    this.isSubmitting = true;
    this.dialogRef.afterClosed().subscribe(() => this.isSubmitting = false);

    // Get the raw data from the form
    const raw: any = this.formGroup.getRawValue();

    // Create the beyond supplier interface and close the dialog
    const intf: BeyondSupplierInterface = {
      id: raw.id,
      email: raw.email,
      firstName: raw.firstName,
      lastName: raw.lastName,
      status: raw.status,
      group: raw.group,
      permissions: raw.permissions,
      companies: raw.companies,
    };
    
    const beyondSupplier = new BeyondSupplier(intf);
    this.dialogRef.close(beyondSupplier);
  }
}
